import { FC, useState } from 'react';
import { Helmet } from 'react-helmet';
import { PublicUtils } from 'components/publicPreviews/publicUtils';
import { useQuery } from 'react-query';
import PostApi from 'api/post';
import { Button, Icon } from '@kontentino/ui';
import { queryKey } from 'constants/queryKey';
import PublicPostPreview from 'components/publicPreviews/post/PublicPostPreview';
import { useTranslation } from 'react-i18next';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons/faArrowLeft';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';

type Props = {
  token: string;
  ids: number[];
};

export const PublicPostPreviewsList: FC<Props> = ({ ids, token }) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);

  const { data } = useQuery(
    queryKey.publicPostPreview({ id: ids[0], token }),
    () => PostApi.publicPreview(ids[0], token),
    {
      onSuccess(data) {
        PublicUtils.setSharedData(data.user);
      },
    },
  );

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === ids.length - 1;

  const pageTitle = data
    ? t('userHasSharedThisPostWithYour', {
        user: data.user.name,
        count: ids.length,
      })
    : null;

  return (
    <div
      className="tw-mx-auto tw-flex tw-w-full tw-max-w-[720px] tw-flex-col tw-gap-4 tw-px-4 tw-py-8 md:tw-py-10"
      data-cy="public-post-preview"
    >
      {pageTitle && (
        <Helmet>
          <title>{`Kontentino | ${pageTitle}`}</title>
        </Helmet>
      )}
      {data && (
        <h1
          className="tw-select-none tw-font-tobias tw-text-2xl tw-font-bold tw-text-primary-100"
          data-cy="public-post-preview_sharedBy"
        >
          {t('userHasSharedThisPostWithYour', {
            user: data.user.name,
            count: ids.length,
          })}
        </h1>
      )}

      {ids.length > 1 && (
        <div className="tw-flex tw-gap-2">
          <Button
            className="tw-flex-1 !tw-bg-white"
            variant="ghost"
            iconBefore={<Icon icon={faArrowLeft} />}
            disabled={isFirst}
            onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
            data-cy="public-post-preview_prev"
          >
            Previous post
          </Button>
          <Button
            className="tw-flex-1 !tw-bg-white"
            variant="ghost"
            iconAfter={<Icon icon={faArrowRight} />}
            disabled={isLast}
            onClick={() =>
              setCurrentIndex((i) => Math.min(ids.length - 1, i + 1))
            }
            data-cy="public-post-preview_next"
          >
            Next post
          </Button>
        </div>
      )}

      <PublicPostPreview
        key={currentIndex}
        id={ids[currentIndex]}
        token={token}
      />
    </div>
  );
};

export default PublicPostPreviewsList;
